import React, { ReactNode } from 'react';
import { PageContainer } from './PageContainer';
import { PageHeader } from './PageHeader';
import { PageToolbar, PageToolbarProps } from './PageToolbar';
import { PageContent, PageContentProps } from './PageContent';


export interface PageLayoutProps extends Partial<PageToolbarProps>, Partial<PageContentProps> {
  // Container props
  className?: string;
  
  // Header props
  title?: string | ReactNode;
  description?: string | ReactNode;
  headerActions?: ReactNode;
  actions?: ReactNode;
  badge?: ReactNode;

  // Custom slots (Nơi chèn KPICard hoặc Alert trước Toolbar)
  beforeToolbar?: ReactNode; 
  
  // Content
  children: ReactNode;
}

/**
 * PageLayout - Siêu Component bọc toàn bộ trang, giúp Developer gọi 1 thẻ duy nhất.
 * Nó hỗ trợ 2 cách dùng:
 * 1. Truyền Props trực tiếp (Nhanh, tự động ghép Header + Toolbar + Content)
 * 2. Compound Component (Dành cho ai thích tự ráp: <PageLayout.Header>, <PageLayout.Toolbar>)
 */
export function PageLayout({
  className,
  title,
  description,
  headerActions,
  actions,
  badge,
  beforeToolbar,
  children,
  isLoading,
  error,
  isEmpty,
  emptyTitle,
  emptyDescription,
  emptyIcon,
  onRetry,
  currentPage,
  totalPages,
  onPageChange,
  ...toolbarProps
}: PageLayoutProps) {
  
  // Kiểm tra xem user có truyền prop nào liên quan đến Toolbar không (search, filter, tabs...)
  const hasToolbarProps = Object.keys(toolbarProps).some(key => 
    toolbarProps[key as keyof PageToolbarProps] !== undefined
  );

  return (
    <PageContainer className={className}>
      
      {/* 1. Phần Tiêu đề trang */}
      {(title || description || headerActions || actions) && (
        <PageHeader 
          title={title} 
          description={description} 
          actions={headerActions ?? actions} 
          badge={badge}
        />
      )}
      
      {/* 2. Phần mở rộng (Dành cho KPI Section) */}
      {beforeToolbar}

      {/* 3. Phần Thanh công cụ tìm kiếm */}
      {hasToolbarProps && <PageToolbar {...toolbarProps} />}
      
      {/* 4. Dữ liệu chính */}
      <PageContent 
        isLoading={isLoading} 
        error={error} 
        isEmpty={isEmpty}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        emptyIcon={emptyIcon}
        onRetry={onRetry}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
      >
        {children}
      </PageContent>

    </PageContainer>
  );
}

// Gắn kèm các thành phần con để hỗ trợ Compound Pattern
PageLayout.Container = PageContainer;
PageLayout.Header = PageHeader;
PageLayout.Toolbar = PageToolbar;
PageLayout.Content = PageContent;
